import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// This is read when the bundle is BUILT, not when the page loads. If it was
// missing then, every call goes to the literal URL "undefined/api/..." and the
// browser reports a network error — which looks like the API is down rather
// than like a missing build variable. Say which it is.
if (!BACKEND_URL) {
  console.error(
    'REACT_APP_BACKEND_URL was not set when this site was built, so the app ' +
      'does not know where its API is. Set it on the static site to the API ' +
      'address (e.g. https://urjapod-api-xxxx.onrender.com) and redeploy with ' +
      'the build cache cleared — the value is compiled in, so a plain redeploy ' +
      'can reuse the old bundle.'
  );
}

export const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = 'urjapod_admin_token';

export const api = axios.create({
  baseURL: API,
  timeout: 90000,
});

api.interceptors.request.use((config) => {
  if (config.url?.includes('/admin/') && config.url !== '/admin/login') {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function billDownloadUrl(signedPath) {
  // /api/bill refuses an unsigned link, and a plain <a href> cannot carry the
  // admin bearer token — so use the expiring signed path the submissions list
  // already returns for each row rather than rebuilding the URL here.
  return signedPath ? `${BACKEND_URL}${signedPath}` : '';
}

/** Wake the API without waiting for it.
 *
 * On a free host the instance sleeps after ~15 minutes, and the first request
 * pays 30-60s to start it. Left until the customer presses Analyze, that lands
 * on top of the extraction. Called when the page loads, it happens while they
 * are still choosing a file. Failures are ignored: this is a nudge, not a
 * dependency.
 */
export function warmUpApi() {
  api.get('/', { timeout: 90000 }).catch(() => {});
}

export async function getConfig() {
  const { data } = await api.get('/config');
  return data;
}

export async function uploadBill({ file }) {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post('/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function getStatus(jobId) {
  const { data } = await api.get(`/status/${jobId}`);
  return data;
}

export async function getResult(jobId) {
  const { data } = await api.get(`/result/${jobId}`);
  return data;
}

export async function submitCustomerDetails(payload) {
  const { data } = await api.post('/customer-details', payload);
  return data;
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------
export async function adminLogin(password) {
  const { data } = await api.post('/admin/login', { password });
  setAdminToken(data.token);
  return data;
}

export async function adminLogout() {
  try {
    await api.post('/admin/logout');
  } finally {
    setAdminToken(null);
  }
}

export async function adminMe() {
  const { data } = await api.get('/admin/me');
  return data;
}

export async function adminChangePassword({ current_password, new_password }) {
  const { data } = await api.post('/admin/change-password', {
    current_password,
    new_password,
  });
  return data;
}

export async function adminListSubmissions(limit = 100) {
  const { data } = await api.get(`/admin/submissions?limit=${limit}`);
  return data;
}

export async function adminForceNotify(jobId) {
  const { data } = await api.post(`/admin/notify/${jobId}`);
  return data;
}

export async function adminQuota() {
  const { data } = await api.get('/admin/quota');
  return data;
}

export async function adminResetQuota(ip) {
  const { data } = await api.post(`/admin/quota/reset?ip=${encodeURIComponent(ip)}`);
  return data;
}
